<template>
  <div ref="card" :class="$style.container">
    <ul :class="$style.listItems">
      <li
        v-for="article in data?.articles"
        :key="article?.fields?.title"
        :class="$style.listItem"
      >
        <AspectRatio ratio="1x1">
          <BasePicture
            v-if="instanceOfAsset(article?.fields?.featuredImage)"
            :src="`${article?.fields?.featuredImage?.fields?.file?.url}?w=80&h=80&fit=thumb`"
            :alt="article?.fields?.featuredImage?.fields?.title || ''"
          />
        </AspectRatio>

        <NuxtLinkLocale
          ref="cardLink"
          :class="$style.listCopy"
          :to="`/news/${article?.fields?.path}`"
        >
          <MarkedParser :md="article?.fields?.title" />
        </NuxtLinkLocale>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import type {
  TypeArticleSkeleton,
  TypeAsideCardWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

const { $client } = useNuxtApp()
const { locale } = useI18n()

const props = defineProps<{
  data: TypeAsideCardWithoutUnresolvableLinksResponse
}>()

const cookieStore = useCookieStore()
const { data } = await useAsyncData(
  `card-related-articles-${cookieStore.languageCookie}-${cookieStore.countryCookie}`,
  async () => {
    const response =
      await $client.withoutUnresolvableLinks.getEntries<TypeArticleSkeleton>({
        content_type: 'article',
        include: 10,
        limit: 3,
        locale: locale.value,
        'fields.tags.sys.id': props.data?.fields?.relatedTag?.sys?.id,
        order: ['-fields.publishedDateTime'],
      })

    return response?.items
  },
  {
    transform(input) {
      return {
        articles: input,
        fetchedAt: useUnixTimestamp(),
      }
    },
    getCachedData: (key) => useCachedData(key),
    watch: [() => cookieStore.languageCookie, () => cookieStore.countryCookie],
  },
)
</script>

<style lang="scss" module>
.container {
  padding: 20px 20px 32px;
  @include fluid-type(21px, 23px);
}

.listItems {
  @include list-unstyled();
}

.listItem {
  display: grid;
  grid-template-columns: 80px minmax(auto, 173px);
  gap: 19px;
  align-items: center;
  padding: 11px 0;

  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.25);
  }

  &:hover,
  &:focus {
    .listCopy {
      color: var(--gray-6);
    }
  }
}

.listCopy {
  font-weight: 900;
  font-size: to-rem(15px);
  line-height: math.div(20, 15);
  text-align: left;
  color: var(--white);
  text-decoration: none;
  cursor: pointer;
}
</style>
